import { StrictMode, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  Clipboard,
  Code2,
  FileCode2,
  Highlighter,
  ListChecks,
  Play,
  Regex,
  Rows3,
  Sparkles,
  XCircle
} from "lucide-react";
import "./styles.css";

type MatchRecord = {
  match: string;
  index: number;
  end: number;
  groups: Array<{ name: string; value: string; index: number }>;
};

type Example = {
  name: string;
  pattern: string;
  sample: string;
  flags: string[];
};

const examples: Example[] = [
  {
    name: "Word + number",
    pattern: String.raw`(?<word>\b[A-Za-z]+\b)\s+(?<count>\d+)`,
    sample: "apple 10\nbanana 42\ncherry 7\ninvalid line\neggfruit 100",
    flags: ["g", "m"]
  },
  {
    name: "Email",
    pattern: String.raw`(?<user>[\w.%+-]+)@(?<domain>[\w.-]+\.[A-Za-z]{2,})`,
    sample: "help@example.com\nbad@@example\njobs@company.co",
    flags: ["g"]
  },
  {
    name: "Date",
    pattern: String.raw`(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})`,
    sample: "Release 2026-06-14\nInvalid 14-06-2026\nDue 2026-12-31",
    flags: ["g"]
  }
];

const flagOptions = ["g", "i", "m", "s", "u", "y"];
const tests = ["pear 15", "watermelon 123", "orange", "plum 9 extra", "berry -5", "apricot 000"];

function App() {
  const [pattern, setPattern] = useState(examples[0].pattern);
  const [sample, setSample] = useState(examples[0].sample);
  const [flags, setFlags] = useState<string[]>(examples[0].flags);
  const [activeMatch, setActiveMatch] = useState(0);
  const [copied, setCopied] = useState("");

  const result = useMemo(() => analyze(pattern, flags, sample), [flags, pattern, sample]);
  const current = result.matches[activeMatch] ?? result.matches[0];
  const snippets = useMemo(() => buildSnippets(pattern, flags, sample), [flags, pattern, sample]);

  async function copyText(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1300);
  }

  function toggleFlag(flag: string) {
    setFlags((currentFlags) => currentFlags.includes(flag) ? currentFlags.filter((item) => item !== flag) : [...currentFlags, flag]);
  }

  function loadExample(example: Example) {
    setPattern(example.pattern);
    setSample(example.sample);
    setFlags(example.flags);
    setActiveMatch(0);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a data-google-vignette="false" className="brand" href="/">
          <span><Regex size={24} /></span>
          <div>
            <h1>RegexTrace Studio</h1>
            <small>Regex debugger, visualizer, and test workbench</small>
          </div>
        </a>
        <nav aria-label="Primary">
          <a data-google-vignette="false" href="#matches">Matches</a>
          <a data-google-vignette="false" href="#groups">Groups</a>
          <a data-google-vignette="false" href="#tests">Tests</a>
        </nav>
        <a data-google-vignette="false" className="utility-link" href="/privacy.html">Privacy</a>
      </header>

      <section className="workspace">
        <aside className="editor-rail">
          <section className="panel">
            <PanelTitle number="1" title="Pattern" icon={<Braces size={18} />} />
            <label className="regex-field">
              <span>Regular expression</span>
              <div>
                <span>/</span>
                <input value={pattern} onChange={(event) => setPattern(event.target.value)} spellCheck={false} />
                <span>/</span>
              </div>
            </label>
            <div className="flag-row" aria-label="Regex flags">
              {flagOptions.map((flag) => (
                <button key={flag} className={flags.includes(flag) ? "active" : ""} type="button" onClick={() => toggleFlag(flag)}>
                  {flag}
                </button>
              ))}
            </div>
          </section>

          <section className="panel">
            <PanelTitle number="2" title="Test string" icon={<FileCode2 size={18} />} />
            <textarea aria-label="Regex test string" value={sample} onChange={(event) => setSample(event.target.value)} spellCheck={false} />
            <div className="sample-meta">
              <span>{sample.split("\n").length} lines</span>
              <span>{sample.length} chars</span>
              <span>{result.matches.length} matches</span>
            </div>
          </section>

          <section className="panel examples">
            <PanelTitle number="3" title="Quick examples" icon={<Sparkles size={18} />} />
            {examples.map((example) => (
              <button key={example.name} type="button" onClick={() => loadExample(example)}>
                <strong>{example.name}</strong>
                <span>{example.pattern}</span>
              </button>
            ))}
          </section>
        </aside>

        <section className="results-panel" id="matches">
          <div className="panel-heading">
            <PanelTitle number="4" title={`Match results (${result.matches.length})`} icon={<Highlighter size={18} />} />
            {result.error ? <span className="error-pill">Syntax error</span> : <button type="button" onClick={() => setActiveMatch((activeMatch + 1) % Math.max(result.matches.length, 1))}><Play size={17} /> Next</button>}
          </div>

          {result.error ? (
            <div className="error-state">
              <AlertTriangle size={30} />
              <strong>{result.error}</strong>
              <p>Fix the pattern before running trace, group, and test-case analysis.</p>
            </div>
          ) : (
            <>
              <div className="highlight-box">
                {renderHighlighted(sample, result.matches, activeMatch, setActiveMatch)}
              </div>

              <section className="group-panel" id="groups">
                <div className="section-heading">
                  <span>Capture groups</span>
                  <strong>{current ? `Match ${activeMatch + 1} of ${result.matches.length}` : "No match selected"}</strong>
                </div>
                <div className="group-table">
                  <div className="table-head"><span>#</span><span>Name</span><span>Value</span><span>Span</span></div>
                  {current ? [{ name: "Entire match", value: current.match, index: 0 }, ...current.groups].map((group, index) => (
                    <div className="group-row" key={`${group.name}-${index}`}>
                      <span>{index}</span>
                      <strong>{group.name}</strong>
                      <code>{group.value || "(empty)"}</code>
                      <span>{index === 0 ? `${current.index}-${current.end}` : "group"}</span>
                    </div>
                  )) : <p className="empty-text">No captures found.</p>}
                </div>
              </section>

              <section className="trace-strip">
                <div className="section-heading">
                  <span>Token trace</span>
                  <strong>How the pattern is parsed</strong>
                </div>
                <div className="token-row">
                  {explainTokens(pattern).map((token, index) => (
                    <article key={`${token.token}-${index}`} className={`token-card ${token.kind}`}>
                      <code>{token.token}</code>
                      <strong>{token.title}</strong>
                      <span>{token.body}</span>
                    </article>
                  ))}
                </div>
              </section>
            </>
          )}
        </section>

        <aside className="explain-rail">
          <section className="panel">
            <PanelTitle number="5" title="Plain-English explanation" icon={<Rows3 size={18} />} />
            <div className="explain-list">
              {explainTokens(pattern).slice(0, 8).map((token, index) => (
                <article key={`${token.title}-${index}`}>
                  <code>{token.token}</code>
                  <div>
                    <strong>{token.title}</strong>
                    <p>{token.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="panel" id="tests">
            <PanelTitle number="6" title="Test cases" icon={<ListChecks size={18} />} />
            <div className="test-list">
              {tests.map((text) => {
                const pass = testPattern(pattern, flags, text);
                return (
                  <article key={text}>
                    <code>{text}</code>
                    <span className={pass ? "pass" : "fail"}>{pass ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{pass ? "Match" : "No match"}</span>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="panel snippet-panel">
            <PanelTitle number="7" title="Code snippets" icon={<Code2 size={18} />} />
            <pre>{snippets}</pre>
            <button type="button" onClick={() => copyText(snippets, "snippets")}><Clipboard size={17} /> {copied === "snippets" ? "Copied" : "Copy snippets"}</button>
          </section>
        </aside>
      </section>

      <section className="seo-panel">
        <div>
          <Regex size={20} />
          <h2>Regex visualizer and capture group tester</h2>
        </div>
        <p>Trace JavaScript regular expressions, inspect named groups, run sample test cases, and copy implementation snippets without uploading data.</p>
        <button type="button" onClick={() => copyText(pattern, "pattern")}><Clipboard size={17} /> Copy regex</button>
      </section>

      <footer>
        <span>RegexTrace Studio evaluates patterns in your browser. Guard very large or untrusted patterns against backtracking risk.</span>
        <nav>
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </main>
  );
}

function analyze(pattern: string, flags: string[], sample: string) {
  try {
    const finalFlags = Array.from(new Set([...flags, "g"])).join("");
    const regex = new RegExp(pattern, finalFlags);
    const matches = [...sample.matchAll(regex)].slice(0, 80).map((match): MatchRecord => ({
      match: match[0],
      index: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      groups: Object.entries(match.groups ?? {}).map(([name, value], index) => ({ name, value: value ?? "", index: index + 1 }))
    })).filter((match) => match.match.length || pattern.length);
    return { error: "", matches };
  } catch (error) {
    return { error: error instanceof Error ? error.message : "Invalid regex", matches: [] };
  }
}

function testPattern(pattern: string, flags: string[], text: string) {
  try {
    const safeFlags = flags.filter((flag) => flag !== "g" && flag !== "y").join("");
    return new RegExp(pattern, safeFlags).test(text);
  } catch {
    return false;
  }
}

function renderHighlighted(text: string, matches: MatchRecord[], active: number, setActive: (index: number) => void) {
  if (!matches.length) return <pre>{text}</pre>;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((match, index) => {
    if (match.index > cursor) nodes.push(<span key={`text-${index}`}>{text.slice(cursor, match.index)}</span>);
    nodes.push(
      <button key={`match-${index}`} className={index === active ? "active-match" : "match-chip"} onClick={() => setActive(index)} type="button">
        {match.match}
      </button>
    );
    cursor = match.end;
  });
  if (cursor < text.length) nodes.push(<span key="tail">{text.slice(cursor)}</span>);
  return <pre>{nodes}</pre>;
}

function explainTokens(pattern: string) {
  const parts = pattern.match(/\(\?<[^>]+>[^)]+\)|\\[bBdDsSwW]|\[[^\]]+\]|\\d\+?|\\w\+?|\\s\+?|\.\*|\.\+|\^|\$|\+|\*|\?|[{}]|[()|]/g) ?? [pattern || "(empty)"];
  return parts.slice(0, 18).map((token) => {
    if (token.startsWith("(?<")) return { token, kind: "group", title: `Named capture ${token.match(/\?<([^>]+)/)?.[1] ?? ""}`, body: "Captures matched text for later reference." };
    if (token === "\\b") return { token, kind: "anchor", title: "Word boundary", body: "Asserts a boundary between word and non-word text." };
    if (token.startsWith("[")) return { token, kind: "class", title: "Character class", body: "Matches one character from the listed set or range." };
    if (token.startsWith("\\d")) return { token, kind: "digit", title: "Digit", body: token.endsWith("+") ? "Matches one or more numeric digits." : "Matches a single numeric digit." };
    if (token.startsWith("\\s")) return { token, kind: "space", title: "Whitespace", body: token.endsWith("+") ? "Matches one or more spaces, tabs, or line breaks." : "Matches a whitespace character." };
    if (token === "^") return { token, kind: "anchor", title: "Start anchor", body: "Requires the match to start at a line or string boundary." };
    if (token === "$") return { token, kind: "anchor", title: "End anchor", body: "Requires the match to end at a line or string boundary." };
    if (["+", "*", "?"].includes(token)) return { token, kind: "quant", title: "Quantifier", body: "Changes how many times the previous token may repeat." };
    return { token, kind: "literal", title: "Literal or group syntax", body: "Part of the exact pattern structure." };
  });
}

function buildSnippets(pattern: string, flags: string[], sample: string) {
  const finalFlags = Array.from(new Set([...flags, "g"])).join("");
  return [
    `// JavaScript`,
    `const regex = /${pattern.replaceAll("/", "\\/")}/${finalFlags};`,
    `const text = ${JSON.stringify(sample.split("\n").slice(0, 3).join("\\n"))};`,
    `for (const match of text.matchAll(regex)) {`,
    `  console.log(match[0], match.groups);`,
    `}`,
    ``,
    `# Python`,
    `import re`,
    `pattern = r"${pattern.replaceAll('"', '\\"')}"`,
    `for match in re.finditer(pattern, text):`,
    `    print(match.groupdict())`
  ].join("\n");
}

function PanelTitle({ number, title, icon }: { number: string; title: string; icon: React.ReactNode }) {
  return (
    <div className="panel-title">
      <span>{number}</span>
      {icon}
      <strong>{title}</strong>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
